import {useCustomQuery, useCustomerId, useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useMemo} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../utils/site-config'
import {useOptionalBrand, toBrandCode} from '../contexts/brand-context'
import {useEnrichVickyProducts} from './use-enrich-vicky-products'
import {useProductAvailability} from './use-product-availability'

/**
 * Unified hook to fetch VickyAI recommendations based on AIModel type.
 * Handles Vicky recommendation types: recommendations, similars, bought-together, shop-the-look, semantic-search, trending.
 *
 * Performance Strategy:
 * 1. Fetch VickyAI data first (has images, variants, descriptions - from backend cache).
 * 2. Reshape against the SFCC master product (via `useEnrichVickyProducts`) so
 *    the result matches a PLP search hit — full image groups for every color,
 *    full variants/variationAttributes/variationGroups, with a
 *    `representedProduct` reference pinning the recommended color.
 * 3. Merge fresh price / promotions / inventory pulled per-variationGroup so
 *    the tile shows the values the shopper will pay.
 * 4. Don't block initial render — show Vicky's payload immediately, enrich
 *    in the background as the SFCC fetches resolve.
 *
 * @param {string} aiModel - Type of recommendation ('recommendations', 'similars', 'bought-together', 'shop-the-look', 'semantic-search', 'trending')
 * @param {string} productSku - Product SKU for product-scoped models, or search query for semantic-search
 * @param {object} [options] - Optional. `availabilityEnabled` (default true) gates the live
 *   availability fetch; a legacy string context arg is tolerated and ignored.
 * @returns {Object} {products, isLoading, productIds}
 */
export const useVickyAIRecommendations = (aiModel, productSku = null, options = {}) => {
    const availabilityEnabled = options?.availabilityEnabled ?? true
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const siteId = site?.id
    const brandId = useOptionalBrand()?.brandId || 'garage'
    const brandCode = toBrandCode(brandId)
    const customerId = useCustomerId()
    const isVickyAIEnabled = getSiteEnvVarValue(appConfig, 'VICKY_ENABLED', siteId) === 'true'
    const localeId = typeof locale === 'string' ? locale : locale?.id
    const countryCode = localeId?.split(/[-_]/).pop()?.toUpperCase() || 'CA'

    // Determine endpoint and parameters based on AIModel
    // Memoize to prevent creating new config object on every render
    const config = useMemo(() => {
        switch (aiModel?.toLowerCase()) {
            case 'skip':
                // Skip fetching - used when we don't have required data yet
                return {
                    endpointPath: '/recommendations',
                    parameters: {},
                    enabled: false
                }
            case 'similars':
            case 'similar':
                return {
                    endpointPath: '/similars',
                    parameters: {
                        siteId: site?.id,
                        c_sku: productSku,
                        c_brand: brandCode
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'bought-together':
            case 'boughttogether':
            case 'frequently-bought-together':
                return {
                    endpointPath: '/bought-together',
                    parameters: {
                        siteId: site?.id,
                        c_sku: productSku,
                        c_brand: brandCode
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'shop-the-look':
            case 'shopthelook':
            case 'shop-the-look-products':
                return {
                    endpointPath: '/shop-the-look',
                    parameters: {
                        siteId: site?.id,
                        c_sku: productSku,
                        c_brand: brandCode
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'semantic-search':
            case 'semanticsearch':
                return {
                    endpointPath: '/semantic-search',
                    parameters: {
                        siteId: site?.id,
                        c_searchQuery: productSku,
                        c_brand: brandCode,
                        c_country: countryCode,
                        ...(localeId ? {locale: localeId} : {})
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'trending':
            case 'trending-products':
                return {
                    endpointPath: '/trending',
                    parameters: {
                        siteId: site?.id,
                        c_brand: brandCode,
                        c_country: countryCode,
                        ...(localeId ? {locale: localeId} : {})
                    },
                    enabled: isVickyAIEnabled && Boolean(site?.id)
                }
            case 'recommendations':
            case 'recommended':
            default:
                return {
                    endpointPath: '/recommendations',
                    parameters: {
                        siteId: site?.id,
                        c_uid: customerId,
                        c_brand: brandCode
                    },
                    /* Wait for the shopper's id (guests have one too). An early `c_uid=anonymous`
                     * request is served from one SCAPI cache entry shared by every guest, then
                     * replaced by the shopper's own list once the id resolves, which reshuffled
                     * the carousel right after it rendered. */
                    enabled: isVickyAIEnabled && Boolean(site?.id) && Boolean(customerId)
                }
        }
    }, [aiModel, site?.id, productSku, customerId, brandCode, countryCode, localeId])

    const {data: recommendations, isLoading} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: config.parameters,
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: config.endpointPath,
                    apiName: 'vickyai'
                }
            },
            rawResponse: false
        },
        {
            // Add explicit query key to prevent cache conflicts between different SKU queries.
            // Each unique combination of (endpoint, SKU(s), siteId) gets its own cache entry.
            queryKey: ['vickyai', config.endpointPath, productSku, site?.id],
            enabled: config.enabled,
            staleTime: 5 * 60 * 1000, // 5 minutes cache
            onError: (err) => {
                console.warn(`VickyAI ${aiModel} fetch failed:`, err)
            }
        }
    )

    /* Vicky's API can return several shapes depending on the endpoint:
     *  - `[product, ...]`              — most endpoints
     *  - `{data: [...]}`               — useCustomQuery wrapper
     *  - `{top_result: [...ids], ...}` — semantic-search (bare variant SKUs)
     * For semantic-search the ids are already shoppable variant SKUs, so we
     * skip the master-derivation enrichment and fetch them directly below. */
    const isSemanticSearch =
        aiModel?.toLowerCase() === 'semantic-search' ||
        aiModel?.toLowerCase() === 'semanticsearch'

    const dataArray = useMemo(() => {
        if (Array.isArray(recommendations)) return recommendations
        if (Array.isArray(recommendations?.data)) return recommendations.data
        if (Array.isArray(recommendations?.top_result))
            return recommendations.top_result.map((id) => ({id}))
        return []
    }, [recommendations])

    const productIds = useMemo(() => dataArray.map((item) => item.id).join(','), [dataArray])

    /* Semantic-search path: top_result is a list of variant SKUs that already
     * resolve to real, orderable products in SFCC. Fetch them directly via
     * SCAPI with the same expand set the master enrichment uses, so the
     * `<ProductTile />` gets images, prices, promotions and variations in
     * one shot — no master lookup, no reshape.
     *
     * SCAPI getProducts caps `ids` at 24 per call; semantic-search can return
     * up to 50. Slice to the cap so the request doesn't 400 and silently
     * return zero tiles. */
    const SCAPI_GET_PRODUCTS_MAX_IDS = 24
    const semanticProductIds = useMemo(() => {
        if (!isSemanticSearch) return ''
        return dataArray
            .slice(0, SCAPI_GET_PRODUCTS_MAX_IDS)
            .map((item) => item.id)
            .filter(Boolean)
            .join(',')
    }, [isSemanticSearch, dataArray])

    const {data: semanticProductsData} = useProducts(
        {
            parameters: {
                ids: semanticProductIds,
                allImages: true,
                perPricebook: true,
                expand: ['images', 'variations', 'prices', 'promotions']
            }
        },
        {
            enabled: isSemanticSearch && !isLoading && Boolean(semanticProductIds),
            keepPreviousData: true,
            staleTime: 5 * 60 * 1000
        }
    )

    /* Live inventory for the semantic-search SKUs via the Shopper Availability API. */
    const {data: semanticAvailabilityMap} = useProductAvailability(
        semanticProductIds ? semanticProductIds.split(',') : [],
        {
            enabled:
                isSemanticSearch &&
                availabilityEnabled &&
                !isLoading &&
                Boolean(semanticProductIds)
        }
    )

    /* SCAPI returns the products unsorted vs the requested ids; re-order so
     * the carousel preserves Vicky's ranking. */
    const semanticProducts = useMemo(() => {
        if (!isSemanticSearch) return []
        const byId = new Map(
            (semanticProductsData?.data || []).map((p) => [p?.id, p]).filter(([id]) => id)
        )
        return semanticProductIds
            .split(',')
            .map((id) => byId.get(id))
            .filter(Boolean)
            .map((product) => {
                const liveInventory = semanticAvailabilityMap?.[product.id]?.inventory
                return liveInventory ? {...product, inventory: liveInventory} : product
            })
    }, [isSemanticSearch, semanticProductsData, semanticAvailabilityMap, semanticProductIds])

    /* Reshape Vicky's variationGroup-shaped items into PLP-style master
     * products (with `representedProduct` pinning the recommended color).
     * The master fetch now also pulls prices / promotions / availability
     * so the tile's `getPriceData` works without a second SFCC call. The
     * shared hook is also used by notify-me, shop-similar and recently-
     * viewed so all surfaces get identical tile behaviour. */
    const enrichedProducts = useEnrichVickyProducts(dataArray, {
        enabled: !isLoading && !isSemanticSearch,
        availabilityEnabled
    })

    return {
        products: isSemanticSearch ? semanticProducts : enrichedProducts,
        /* Only the initial fetch, never enrichment — and never a disabled query:
         * react-query v4 parks those in `loading`, which would pin a consumer's
         * skeleton on forever whenever VICKY_ENABLED is off. */
        isLoading: config.enabled && isLoading,
        productIds
    }
}
